<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { fade } from 'svelte/transition';
    import { get } from 'svelte/store';

    import type { TacticalPlanRuleResponseType } from './TacticalPlanRuleApi';
    import LastmileFilter from '../../shared/LastmileFilter.svelte';
    import type { VehicleResponseType } from '../Vehicles/types';
    import ZoneList from '../Zones/ZoneList.svelte';
    import {
        currentZoneList,
        debug,
        tacticalPlanRuleCurrent,
        tacticalPlanStore,
    } from '../../../stores/store';
    import { onMount, afterUpdate } from 'svelte';
    import { openModal } from 'svelte-modals';
    import ModalRemoveSomething from '../Modals/ModalRemoveSomething.svelte';
    import { TacticalPlanRule } from './TacticalPlanRule';
    import { api, tactical_plan_rules_filter_config } from '../../../stores/api';
    import { FilterManagerIO } from '../telematics/components/FilterByParameters/FilterManagerIO';
    import {
        explodeAndOr,
        implodeAndOr,
        type localStorageFilterType,
    } from '../telematics/components/FilterByParameters/FilterManager';
    import { waitForZoneSave } from './waitForZoneSave';
    import IconCopy from '../../shared/Icons/IconCopy.svelte';
    import IconRemove from '../../shared/Icons/IconRemove.svelte';
    import StyleLikeFilterItem from '../../shared/StyleLikeFilterItem.svelte';
    import ButtonZoneLock from '../Filter/ButtonZoneLock.svelte';
    import { TacticalPlanRuleApi } from './TacticalPlanRuleApi';
    import { type QueriesType } from '../telematics/types';
    import ErrorInline from '../../shared/ErrorInline.svelte';
    import type { SimpleZoneType } from '../Zones/ZoneApi';
    import type { ValidatorReport } from './ValidatorReport';
    import ModalError from '../../shared/Modals/ModalError.svelte';
    import IconPaste from '../../shared/Icons/IconPaste.svelte';
    import SuccessColor from '../../shared/SuccessColor.svelte';
    import IconLongArrow from '../../shared/Icons/IconLongArrow.svelte';
    import InputTextInline from '../../shared/InputTextInline.svelte';
    import type { ZoneType } from '../Zones/types';
    import SelectVehicle from '../Vehicles/SelectVehicle.svelte';
    import IconStylusAsButton from '../../shared/Icons/IconStylusAsButton.svelte';
    import IconPending from '../../shared/Icons/IconPending.svelte';
    import WarningInline from '../../shared/WarningInline.svelte';
    import InfoInline from '../../shared/InfoInline.svelte';
    import IconRuleAutoConfirm from './IconRuleAutoConfirm.svelte';
    import { MenuContextCustomActions } from '../MenuDropdown/MenuContextCustomActions';
    import MenuContextIcon from '../MenuDropdown/MenuContext/MenuContextIcon.svelte';
    import { features } from '../../shared/Features/features';
    import { ZoneEditor } from '../Zones/ZoneEditor';
    import Switch from '../../shared/Switch.svelte';
    import VisibilitySwitch from '../../shared/VisibilitySwitch.svelte';
    import TacticalPlanRuleMapControl from './TacticalPlanRuleMapControl.svelte';
    import ButtonRoundCancel from '../../shared/ButtonRoundCancel.svelte';
    import { app } from '../../../stores/app';

    export let rule: TacticalPlanRule;
    export let index: number;
    export let vehicles: VehicleResponseType[] = [];
    export let onRemove: (rule: TacticalPlanRule) => void;
    export let addRuleBeforeThisRule: (
        rule: TacticalPlanRule,
        deepcopy: boolean,
        currentPriority: number | null
    ) => Promise<TacticalPlanRule | undefined>;
    export let tacticalPlanValidatorReport: ValidatorReport;
    export let simpleZones: SimpleZoneType[] | null = null;
    export let alreadyUsedVehicles: number[] = [];
    export let zonesVisible: boolean = true;
    export let onZoneVisibleExclusive: (ruleId: number) => void = (ruleId: number) => {};

    let mapId: number | null = null;
    let hoverRuleHeader: boolean = false;
    let hoverRuleBody: boolean = false;
    // let triggerZonesSave: boolean = false;
    let onChangeTelematicsFilterCounter: number = 0;
    let zones: ZoneType[] = [];
    let loadingZones: boolean = false;

    let hoverRuleNameEdit: boolean = false;
    let editingRuleName: string = '';

    let zoneEditor: ZoneEditor | null = null;

    export let readOnly: boolean;

    let saved = true;
    let allowToChangeRuleName: boolean = false;

    let showButtonAddFilter: boolean = true;
    let query: QueriesType = {};

    let lastListedVehicleName: string = '';

    let menu: MenuContextCustomActions = new MenuContextCustomActions();

    let mapControl: TacticalPlanRuleMapControl|null = null;

    function updateMenu(menu: MenuContextCustomActions) {
        menu.reset();
        menu.addAction({
            id: 'copy',
            label: 'Copy',
            disabled: false,
            visible: !rule.isAlwaysLast(),
            icon: IconCopy,
            onClick: () => {
                $tacticalPlanStore.ruleToCopy = new TacticalPlanRule(rule.getRule(), false);
                $tacticalPlanStore.ruleToCopy.setInnerId(rule.getInnerId());
                updateMenu(menu);
            },
            subMenuIsOpen: false,
        });
        menu.addAction({
            id: 'paste',
            label: 'Paste',
            disabled: $tacticalPlanStore.ruleToCopy === null,
            visible: true,
            icon: IconPaste,
            onClick: () => {
                $tacticalPlanRuleCurrent = null;
                $tacticalPlanStore.triggerStartSaveThisRuleByInnerId = '';
                waitForZoneSave(() => {
                    if ($tacticalPlanStore.ruleToCopy !== null) {
                        addRuleBeforeThisRule(
                            $tacticalPlanStore.ruleToCopy,
                            true,
                            rule.getPriority()
                        );
                        $tacticalPlanStore.ruleToCopy = null;
                        updateMenu(menu);
                    } else {
                        openModal(ModalError, { message: $_('error_while_pasting_rule') });
                    }
                });
            },
            subMenuIsOpen: false,
        });
        menu.addAction({
            id: 'remove',
            label: 'Remove',
            disabled: rule.isAlwaysLast(),
            visible: !rule.isAlwaysLast(),
            icon: IconRemove,
            onClick: () => {
                $tacticalPlanRuleCurrent = null;
                $tacticalPlanStore.triggerStartSaveThisRuleByInnerId = '';
                waitForZoneSave(() => {
                    onRemoveWithConfirm(rule);
                    updateMenu(menu);
                });
            },
            subMenuIsOpen: false,
        });
        menu = menu;
    }

    async function updateRule(rule: TacticalPlanRule) {
        let r: TacticalPlanRuleResponseType | void;
        if (rule.isNewRule()) {
            r = await TacticalPlanRuleApi.createOne(get(api), rule.getRuleReadyToRequest()).catch(
                e => {
                    openModal(ModalError, { message: $_('error_while_creating_rule') });
                }
            );
            if (r) {
                rule.setRule(r, false);
                rule = rule;
            } else {
                openModal(ModalError, { message: $_('error_while_creating_rule') });
            }
        } else if (rule.getId() !== null) {
            r = await TacticalPlanRuleApi.updateOne(
                get(api),
                rule.getId(),
                rule.getRuleReadyToRequest()
            ).catch(e => {
                openModal(ModalError, { message: $_('error_while_updating_rule') });
            });
            if (r) {
                rule.setRule(r, false);
                rule = rule;
            } else {
                openModal(ModalError, { message: $_('error_while_updating_rule') });
            }
        }
    }

    function onRemoveWithConfirm(rule: TacticalPlanRule) {
        openModal(ModalRemoveSomething, {
            text: $_('you_sure_to_remove_this_rule?'),
            name: '', // rule.getRule().name,
            onClick: () => {
                onRemove(rule);
            },
        });
    }

    const filterManagerIO = new FilterManagerIO(
        async () => {
            return new Promise((resolve, reject) => {
                // console.log('TelematicsFilter, load, rule.getRule().details.filter', rule.getRule().details.filter);
                let { queries: tempQueries, __andOrSelection } = explodeAndOr(
                    rule.getRule().details.filter
                );
                let data: localStorageFilterType = {
                    mainFilterData: { queries: tempQueries, related: [] },
                    lastUsedFilterData: { queries: {}, related: [] },
                    __inactiveQueriesKeys: [],
                    __andOrSelection: __andOrSelection,
                };
                // console.log('TelematicsFilter, load, rule', data);
                resolve(data);
            });
        },
        async data => {
            // console.log('TelematicsFilter, save, rule', data.mainFilterData.queries);
            rule.getRule().details.filter = implodeAndOr(
                data.mainFilterData.queries,
                data.__andOrSelection
            );
            rule = rule;
        }
    );

    function createMapControl() {
        let mapControlElement = document.createElement('div');
        //mapControlElement.style.cssText = 'width: 100%; height: 34px; background-color: rgba(255, 255, 255, 0.9);';
        let shadow = mapControlElement.attachShadow({mode: 'open'});
        Targeo.Base.Map.modules.Controls.addControl(
            'tactical-plan-rule-map-control',
            mapControlElement,
            {
                align: 'bottom',
                edge: false,
                visibilityPriority: 1000,
                attractPriority: 1000,
                onSpaceShortage: (width: number|boolean) => {
                    if (width) {
                        mapControlElement.style.maxWidth = width+'px';
                    } else {
                        mapControlElement.style.maxWidth = '';
                    }
                    return {};
                }
            }
        );
        Targeo.Base.Map.modules.Controls.updateControl('logo', {visible: false});
        mapControl = new TacticalPlanRuleMapControl({
            target: shadow,
            props: {
                showGridModeSwitch: $features?.feature_micro_grid || $app.account?.getSettingsReadonly()?.micro_grid,
                gridMode: false,
                volume: $tacticalPlanStore.volume,
                zoneEditor: zoneEditor,
                onOptionChange: (option: string, value: unknown) => {
                    if (zoneEditor) {
                        switch (option) {
                            case 'gridMode':
                                if (value) {
                                    zoneEditor.gridModeStart();
                                } else {
                                    zoneEditor.gridModeClose();
                                }
                                break;
                            case 'volume':
                                $tacticalPlanStore.volume = value;
                                localStorage.setItem('zoneEditorJobVolume', JSON.stringify({value: value, expire: (new Date()).getTime() + 24*3600*1000}));
                                zoneEditor.gridSetVolume($tacticalPlanStore.volume);
                                break;
                        }
                    }
                    setTimeout(() => { Targeo.Base.Map.modules.Controls.updateControl('tactical-plan-rule-map-control', {}); }, 0);
                },
                onSave: () => {
                    removeMapControl();
                    readOnly = true;
                    $tacticalPlanRuleCurrent = null;
                    $tacticalPlanStore.triggerZonesSave = true;
                    simpleZones = null;
                    updateRule(rule);
                },
                onDiscard: () => {
                    removeMapControl();
                    saved = true;
                    readOnly = true;
                    $tacticalPlanRuleCurrent = null;
                    $tacticalPlanStore.triggerZonesSave = true;
                    simpleZones = null;
                    updateRule(rule);
                },
                onRender: () => {
                    Targeo.Base.Map.modules.Controls.updateControl('tactical-plan-rule-map-control', {visible: true});
                    setTimeout(() => { Targeo.Base.Map.modules.Controls.updateControl('tactical-plan-rule-map-control', {}); }, 0);
                }
            }
        });
    }

    function removeMapControl() {
        Targeo.Base.Map.modules.Controls.removeControl('tactical-plan-rule-map-control');
        Targeo.Base.Map.modules.Controls.updateControl('logo', {visible: true});
        mapControl = null;
    }

    onMount(() => {
        return () => {
            if (mapControl) {
                removeMapControl();
            }
        }
    });

    $: {
        rule = rule;
        showButtonAddFilter =
            Object.keys(explodeAndOr(rule.getRule().details.filter).queries).length === 0 ||
            hoverRuleHeader ||
            hoverRuleBody;
        lastListedVehicleName =
            vehicles.find(v => v.id === rule.getRule().details.vehicle_id)?.name || '';
    }

    $: {
        readOnly = readOnly;
        if (!readOnly && !zonesVisible) {
            zonesVisible = true;
            zoneEditor?.toggleVisibility(true);
        }
        if (!readOnly && !mapControl) {
            createMapControl();
        }
    }

    $: {
        zoneEditor?.toggleVisibility(zonesVisible);
    }

    afterUpdate(() => {
        if (rule.getRule().details.action.type === 'map') {
            currentZoneList.setZonesByRuleId(
                rule.getId() ?? -1,
                rule.getRule().details.action?.map_id ?? -1,
                zones
            );
            // console.log('currentZoneList, mapId', rule.getId(), rule.getRule().details.action?.map_id, zones);
        }
        updateMenu(menu);
    });
</script>

<div class="item-container" class:hoveredRule={hoverRuleHeader && !rule.isAlwaysLast()}>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        class="rule-header"
        on:mouseenter={() => (hoverRuleHeader = true)}
        on:mouseleave={() => (hoverRuleHeader = false)}
    >
        <div>
            <Switch checked={rule.getEnabled()} on:change={e => {
                rule.setEnabled(e.detail.checked);
                rule = rule;
                updateRule(rule);
            }} />
        </div>
        <div
            style="position: relative; display: flex; align-items: center; column-gap: 5px;"
            on:mouseenter={() => {
                hoverRuleNameEdit = true;
            }}
            on:mouseleave={() => {
                hoverRuleNameEdit = false;
            }}
        >
            <div
                class="insert-rule"
                class:insert-rule-clickable={!rule.isAlwaysLast()}
                on:click={() => {
                    $tacticalPlanRuleCurrent = null;
                    $tacticalPlanStore.triggerStartSaveThisRuleByInnerId = '';
                    waitForZoneSave(() => {
                        if (!rule.isAlwaysLast()) {
                            editingRuleName = rule.getName() || '';
                            allowToChangeRuleName = true;
                        }
                    });
                }}
                title={rule.isAlwaysLast() || !hoverRuleNameEdit || rule.getName() === ''
                    ? ''
                    : $_('edit_rule_name')}
            >
                <div
                    style="padding:0 4px 0 0; display: flex; flex-direction: row; align-items: center; column-gap: 6px;"
                    class="dont-break"
                >
                    {#if rule.isAlwaysLast()}
                        {$_('other')}
                    {:else}
                        #{index + 1}
                        {#if allowToChangeRuleName}
                            <InputTextInline
                                value={editingRuleName}
                                styleMode="white"
                                editMode={true}
                                onChange={v => {
                                    allowToChangeRuleName = false;
                                    rule.setName(v);
                                    rule = rule;
                                    updateRule(rule);
                                }}
                            />
                        {:else if rule.getName()}
                            {` ` + rule.getName()}
                        {:else if hoverRuleNameEdit}
                            <div
                                style="opacity: 0.85; font-weight: 400;"
                                in:fade={{ duration: 200 }}
                                out:fade={{ duration: 200 }}
                            >
                                {$_('add_rule_name_placeholder')}
                            </div>
                        {/if}
                    {/if}
                </div>
            </div>
        </div>
        <IconRuleAutoConfirm bind:rule />
        {#if !rule.isAlwaysLast()}
            <VisibilitySwitch checked={zonesVisible} scale={0.7} on:change={e => {
                zonesVisible = e.detail.checked;
                zoneEditor?.toggleVisibility(zonesVisible);
            }} on:click={e => {
                if (e.detail.event.shiftKey) {
                    onZoneVisibleExclusive(rule.getId());
                }
            }}/>
        {/if}

        {#if $debug}
            ruleId: {rule.getId()}, priority: {rule.getPriority()}
        {/if}

        <div
            class="sortable-item-handle {rule.isAlwaysLast() ? '' : 'draggable-rule'}"
            style="display: flex; flex: 1 0 auto; height: 20px; align-items: center;"
        >
            <div class="line1"></div>
        </div>
        {#if !rule.isAlwaysLast()}
            {#if $tacticalPlanStore.ruleToCopy !== null && $tacticalPlanStore.ruleToCopy.getInnerId() === rule.getInnerId()}
                <div in:fade={{ duration: 500 }} out:fade={{ duration: 500 }}>
                    <SuccessColor>
                        <div
                            on:click={() => {
                                $tacticalPlanStore.ruleToCopy = null;
                            }}
                            style="padding: 0 4px 0 4px; text-transform: uppercase;"
                        >
                            SKOPIOWANE
                        </div>
                    </SuccessColor>
                </div>
            {/if}
        {/if}

        {#if hoverRuleHeader || hoverRuleBody || menu.isOpen}
            <!--            <MenuContextCustom {menu} />-->
            <MenuContextIcon bind:menu />
        {/if}
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        class="rule-body"
        on:mouseenter={() => (hoverRuleBody = true)}
        on:mouseleave={() => (hoverRuleBody = false)}
    >
        <div class="rule-body-left">
            {#if tacticalPlanValidatorReport.hasMessagesForRuleId(rule.getId()) && $features?.feature_hide_errors_for_tactical_plan !== true}
                <div class="rule-messages">
                    {#each tacticalPlanValidatorReport.getMessagesForRuleId(rule.getId(), 'warning') as reportItem}
                        <WarningInline
                            text={$_(reportItem.message, {
                                values: { vehicle: lastListedVehicleName },
                            })}
                        />
                    {/each}
                    {#each tacticalPlanValidatorReport.getMessagesForRuleId(rule.getId(), 'error', true) as reportItem}
                        <ErrorInline
                            text={$_(reportItem.message, {
                                values: { vehicle: lastListedVehicleName },
                            })}
                            on:click={() => {
                                if (reportItem.details?.markable) {
                                    zoneEditor?.markConflict(reportItem.zoneId, reportItem.details)
                                }
                            }}
                        />
                    {/each}
                    {#each tacticalPlanValidatorReport.getMessagesForRuleId(rule.getId(), 'info') as reportItem}
                        <InfoInline
                            text={$_(reportItem.message, {
                                values: { vehicle: lastListedVehicleName },
                            })}
                        />
                    {/each}
                </div>
            {/if}

            {#if $debug}
                errors: {JSON.stringify(rule.getErrors())}
            {/if}
            {#if !rule.isAlwaysLast()}
                <div
                    class="item-filter"
                    on:focusin={() => {
                        console.log('Telematics filter focused - disable rule editing');
                        removeMapControl();
                        readOnly = true;
                        $tacticalPlanRuleCurrent = null;
                        $tacticalPlanStore.triggerZonesSave = true;
                        simpleZones = null;
                        updateRule(rule);
                    }}
                >
                    <LastmileFilter
                        collectionName="rules"
                        onChange={(queries, andOrSelector) => {
                            /* console.log(
                                'Telematics filter onChange',
                                implodeAndOr(queries, andOrSelector)
                            );
                             */
                            rule.getRule().details.filter = implodeAndOr(queries, andOrSelector);
                            if (onChangeTelematicsFilterCounter > 0) {
                                // dont save init state
                                // console.log('Telematics filter onChange, updateRule');
                                updateRule(rule);
                            }
                            rule = rule;
                            onChangeTelematicsFilterCounter++;
                        }}
                        filterConfig={$tactical_plan_rules_filter_config}
                        enableInactiveQueries={false}
                        visibleFilterIcon={false}
                        andOrSelectionEnabled={true}
                        {filterManagerIO}
                        showButtonRemoveAll={false}
                        {showButtonAddFilter}
                    />
                </div>
            {/if}

            <div class="item-action-select">
                <div class="item-action-select-arrow">
                    <IconLongArrow />
                </div>

                <div class="item-action-select-content">
                    <div
                        style="display: flex; flex-direction: row; column-gap: 10px; align-items: center; flex-wrap: wrap; row-gap: 10px;"
                    >
                        {#if rule.getRule().details.action.type === 'vehicle'}
                            <StyleLikeFilterItem mode="vehicle">
                                <SelectVehicle
                                    selected={rule.getRule().details.action?.vehicle_id}
                                    {alreadyUsedVehicles}
                                    onChange={vehicle_id => {
                                        console.log('Zmieniono pojazd', vehicle_id);
                                        if (vehicle_id !== null) {
                                            // TODO: add removing existing vehicle from other rules
                                        }
                                        rule.setVehicleId(vehicle_id);
                                        rule = rule;
                                        updateRule(rule);
                                        return true;
                                    }}
                                />
                            </StyleLikeFilterItem>
                        {/if}
                        {#if rule.getRule().details.action.type === 'map'}
                            <div
                                class="zones {zones.length === 0
                                    ? 'zones-tiny'
                                    : ''} {hoverRuleBody || hoverRuleHeader || !readOnly
                                    ? 'zones-border'
                                    : ''}"
                            >
                                <ZoneList
                                    mode="simple"
                                    bind:readOnly
                                    bind:rule
                                    bind:saved
                                    {updateRule}
                                    bind:alreadyUsedVehicles
                                    bind:zones
                                    bind:loading={loadingZones}
                                    bind:zoneEditor
                                    {tacticalPlanValidatorReport}
                                />
                                {#if zones.length === 0}
                                    <StyleLikeFilterItem mode="vehicle">
                                        <SelectVehicle
                                            selected={rule.getVehicleId()}
                                            {alreadyUsedVehicles}
                                            onChange={vehicle_id => {
                                                console.log('Zmieniono pojazd', vehicle_id);
                                                rule.setVehicleId(vehicle_id);
                                                rule = rule;
                                                updateRule(rule);
                                                return true;
                                            }}
                                        />
                                    </StyleLikeFilterItem>
                                {/if}
                            </div>
                            <div class="toggle-buttons">
                                {#if loadingZones}
                                    <IconPending spin={true} />
                                {:else if readOnly && (hoverRuleBody || hoverRuleHeader)}
                                    <IconStylusAsButton
                                        title={$_('edit_zones')}
                                        on:click={() => {
                                            loadingZones = true;
                                            setTimeout(() => {
                                                readOnly = false;
                                                $tacticalPlanRuleCurrent = rule.getInnerId();
                                                simpleZones = null;
                                                loadingZones = false;
                                            }, 0);
                                        }}
                                    />
                                {:else if !readOnly}
                                    <ButtonZoneLock
                                        bind:saved
                                        on:click={() => {
                                            removeMapControl();
                                            readOnly = true;
                                            $tacticalPlanRuleCurrent = null;
                                            $tacticalPlanStore.triggerZonesSave = true;
                                            simpleZones = null;
                                            updateRule(rule);
                                        }}
                                    />
                                    <ButtonRoundCancel title={$_('discard_edit_zone')} opacity={0.5} on:click={() => {
                                        removeMapControl();
                                        saved = true;
                                        readOnly = true;
                                        $tacticalPlanRuleCurrent = null;
                                        $tacticalPlanStore.triggerZonesSave = true;
                                        simpleZones = null;
                                        updateRule(rule);
                                    }}/>
                                {/if}
                            </div>
                        {/if}
                    </div>
                    <div>
                        {#if rule.getRule().details.action.type === 'map' && !readOnly}
                            <div
                                in:fade={{ duration: 500, delay: 100 }}
                                out:fade={{ duration: 500, delay: 100 }}
                                style="font-size: 12px; padding: 10px 0 5px 0; max-width: 355px; visibility:{hoverRuleBody
                                    ? 'visible'
                                    : 'visible'}"
                            >
                                ({$_('how_to_create_first_zone')})
                            </div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    .item-container {
        padding: 5px 5px;
        border-radius: 6px;
        box-sizing: border-box;
    }

    .rule-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        column-gap: 10px;
        cursor: pointer;
    }

    .rule-messages {
        padding: 1px 0 1px 0;
    }
    .rule-messages>:global(div:last-child) {
        margin-bottom: 6px;
    }

    .draggable-rule {
        cursor: move;
    }
    .rule-body {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        font-size: var(--font-size-tactical-plan);
    }
    .rule-body-left {
        display: flex;
        padding: 8px 0;
        flex-direction: column;
        width: 100%;
    }

    /*.hoveredRule {*/
    /*    transition: 0.2s;*/
    /*    background-color: var(--main-selected-background);*/
    /*}*/
    .line1 {
        border-top: 1px dashed #0009;
        flex: 1 1 100%;
    }
    .insert-rule {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        background-color: #000;
        border-radius: 14px;
        height: 21px;
        color: white;
        /*min-width: 70px;*/
        /*cursor: pointer;*/
        column-gap: 4px;
        padding: 0 12px;
        font-weight: 600;
        font-size: 11px;
    }
    .toolbar {
        display: flex;
        visibility: hidden;
        justify-content: space-between;
        flex-direction: column;
        align-items: center;
        padding: 4px;
        background-color: #fffc;
    }
    .toolbarVisible {
        visibility: visible;
        transition: 0.2s;
    }

    .item-action-select {
        display: flex;
        align-items: flex-start;
        flex-wrap: wrap;
        row-gap: 10px;
        /*max-width: 300px;*/
    }
    .item-action-select-arrow {
        display: flex;
        align-items: center;
        column-gap: 0;
        flex: 0 0 40px;
    }
    .item-action-select-content {
        display: flex;
        flex-direction: column;
        flex: 1 1 200px;
    }

    .zones {
        /*border-right: 1px solid var(--automapa-blue);*/
        border-right: 1px solid transparent; /* changed due to email from RM */
        min-width: 363px;
        min-height: 30px;
        display: flex;
        align-items: center;
    }
    .zones-tiny {
        min-width: 200px;
    }
    .zones.zones-border {
        transition: 0.3s;
        border-right: 1px solid #b6bfdc; /* changed due to email from RM */
    }
    .toggle-buttons {
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-width: 30px;
        position: sticky;
        top: 10px;
        bottom: 10px;
    }

    .hidden {
        visibility: hidden;
    }

    .insert-rule-clickable {
        cursor: pointer;
    }

    .item-filter {
        margin-bottom: 10px
    }
</style>
